import React, { useCallback, useEffect, useState } from "react";
import { Heart, Loader2, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import api from "../console/api";
import { CrushRequestCard } from "./crush/CrushRequestCard";
import { CrushDemandTable } from "./crush/CrushDemandTable";

export const STATUS_OPTIONS = [
  ["received", "♡ Crush received"], ["checking", "🌱 Checking the mother plant"], ["rooting", "💚 Rooting for you"],
  ["match", "💌 It's a match!"], ["ready", "🌸 Ready for its new home"], ["declined", "🥀 Not this season"],
];

export default function AdminCuttingRequests() {
  const [tab, setTab] = useState("requests");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [demand, setDemand] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, d] = await Promise.all([
        api.get(`/admin/crush/requests?status=${status}&q=${encodeURIComponent(q)}`),
        api.get("/admin/crush/demand"),
      ]);
      setRows(r.data || []); setDemand(d.data || []);
    } catch { toast.error("Couldn't load cutting requests."); }
  }, [status, q]);
  useEffect(() => { load(); }, [load]);

  const counts = (rows || []).reduce((m, r) => ({ ...m, [r.status]: (m[r.status] || 0) + 1 }), {});

  return (
    <div className="space-y-5" data-testid="admin-cutting-requests">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase font-semibold text-[#6E1E3A]">The Cutting Crush Club</div>
          <h1 className="font-serif text-3xl text-[#3C2A1E] mt-1 flex items-center gap-2"><Heart className="w-6 h-6 text-[#6E1E3A]" fill="#6E1E3A" /> Cutting Requests</h1>
          <p className="text-sm text-[#7A6A55] mt-1 max-w-2xl">Expressions of interest only , nothing here touches stock. Approve lines, set a price, then convert to an unpaid order in Awaiting Payment. Every status change can message the customer in HQ.</p>
        </div>
        <button type="button" onClick={load} className="h-9 px-3 rounded border border-[#E8DFCB] bg-white text-xs inline-flex items-center gap-2" data-testid="crush-admin-reload"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
      </header>

      <div className="flex flex-wrap gap-2">
        {[["requests", "Requests"], ["demand", "Variety demand"]].map(([id, label]) => (
          <button key={id} type="button" onClick={() => setTab(id)} data-testid={`crush-admin-tab-${id}`}
            className={`h-9 px-4 rounded-full text-[10px] tracking-[0.2em] uppercase font-bold border ${tab === id ? "bg-[#3E1F46] text-white border-[#3E1F46]" : "bg-white border-[#E8DFCB] text-[#3C2A1E]"}`}>{label}</button>
        ))}
      </div>

      {tab === "demand" ? <CrushDemandTable rows={demand} onToggle={load} /> : (
        <>
          <div className="flex flex-wrap items-center gap-2 bg-white border border-[#E8DFCB] rounded-xl p-3">
            <label className="flex items-center gap-2 border border-[#E8DFCB] rounded-full px-3 h-9 flex-1 min-w-[220px]">
              <Search className="w-4 h-4 text-[#9C8B73]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, ref or variety" className="flex-1 outline-none text-sm" data-testid="crush-admin-search" />
            </label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 px-3 border border-[#E8DFCB] rounded-full text-xs bg-white" data-testid="crush-admin-status-filter">
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}{counts[v] ? ` (${counts[v]})` : ""}</option>)}
            </select>
            <span className="text-xs text-[#7A6A55]" data-testid="crush-admin-count">{rows ? `${rows.length} request${rows.length === 1 ? "" : "s"}` : ""}</span>
          </div>
          {rows === null ? <Loader2 className="w-5 h-5 animate-spin text-[#6E1E3A]" /> : rows.length === 0 ? (
            <div className="bg-white border border-dashed border-[#E8DFCB] rounded-xl p-10 text-center text-sm text-[#7A6A55]" data-testid="crush-admin-empty">No cutting requests yet. When a customer shoots their shot, it lands here.</div>
          ) : rows.map((r) => <CrushRequestCard key={r.id} r={r} reload={load} />)}
        </>
      )}
    </div>
  );
}
